import React, { useState } from "react";
import { DateTime } from "luxon";
import {
  useAuthentication,
  useProjectStatusesRetrieval,
  useRetrieveCurrentPlanzone,
  useRetrievePersonalTimeSheet,
  useRetrievePlanzoneSettings,
  useRetrieveResourceSubordinates,
  useRetrieveSecondaryCriteriaTemplate,
} from "@planzone/view-models";
import { TimeSheetFilter } from "@planzone/dto";

import { usePreferences } from "@components/preferences";
import { Flag, InfiniteScroll } from "@common/infiniteScroll";
import { Controls, TreeToggle } from "@common/treeToggle";
import {
  getCurrentWeek,
  getMonth,
  initCursor,
  formatLocalDate,
} from "@pages/sharedTimeSheetUtils";
import { Navigation } from "./navigation";
import { Month } from "./month";
import { Week } from "./week";
import { Root } from "./root";
import { Total } from "./total";
import { Loader } from "./loader";
import { ResourceTimeOverview } from "./resourceTimeOverview";
import { DetailsToggle } from "./detailsToggle";
import { DisplayToggle } from "./displayToggle";
import { TeamAllocations } from "./teamAllocations";
import { WeekTimeRecords } from "@scenes/weekTimeRecords";
import { Filters } from "./filters";
import { Props } from "./types";
import { AutoFocusContext } from "./autoFocusCtx";
import styles from "./styles.scss";
import { useModal } from "@root/client/modal";
import { UseCases } from "@planzone/core";
import { useTranslation } from "react-i18next";

function TimeSheet(_: Props) {
  const { account } = useAuthentication();
  const { pushView } = useModal();
  const { t } = useTranslation();

  const [cursor, setCursor] = React.useState<DateTime>(initCursor());
  const { state, edit } = usePreferences();
  const {
    isRequestPending: isProjectStatusesLoading,
    isRequestSuccess: isProjectStatusesSuccessful,
    isRequestFailure: isProjectStatusesFailed,
    statuses,
    retrieveProjectStatuses,
  } = useProjectStatusesRetrieval();
  const { personalTimeSheetFilter } = state;
  const setFilter = (nextFilter: TimeSheetFilter) =>
    edit({ ...state, personalTimeSheetFilter: nextFilter });
  const month = getMonth(cursor);
  const { week, index: weekIndex } = getCurrentWeek(cursor, month);
  const filter =
    !personalTimeSheetFilter.projectStatuses ||
    personalTimeSheetFilter.projectStatuses.length === 0
      ? {
          ...personalTimeSheetFilter,
          minDate: formatLocalDate(week[0]),
          maxDate: formatLocalDate(week[week.length - 1]),
          projectStatuses: statuses.filter(
            (status) => status.label !== "pz_closed",
          ),
        }
      : {
          ...personalTimeSheetFilter,
          minDate: formatLocalDate(week[0]),
          maxDate: formatLocalDate(week[week.length - 1]),
        };
  const {
    isRequestPending,
    isDirty,
    isRequestFailure: isTimeSheetFailure,
    canLoadMore,
    timeSheet,
    currentPage,
    retrievePersonalTimeSheet,
  } = useRetrievePersonalTimeSheet(account.resource, filter, 30);
  const [showPersonalView, setShowPersonalView] = useState(true);
  const [lastAllocated, setLastAllocated] = useState<{
    taskUuid: string;
    projectUuid: string;
    resourceUuid: string;
  } | null>(null);
  const {
    retrieveResourceSubordinates,
    isRequestSuccess: teamRequestSuccess,
    isRequestPending: teamRequestPending,
    resources,
    canLoadMore: canLoadMoreSubordinates,
    currentPage: subordinatesCurrentPage,
  } = useRetrieveResourceSubordinates(account.resource);
  const { planzone } = useRetrieveCurrentPlanzone();
  const {
    settings,
    retrievePlanzoneSettings,
    isRequestSuccess: retrievePlanzoneSettingsSuccess,
    isRequestPending: retrievePlanzoneSettingsPending,
  } = useRetrievePlanzoneSettings(planzone);
  const {
    isRequestPending: isTimeRecordsTemplatePending,
    isRequestSuccess: isTimeRecordsTemplateSuccess,
    isRequestFailure: isTimeRecordsTemplateFailure,
    retrieveSecondaryCriteriaTemplate,
  } = useRetrieveSecondaryCriteriaTemplate(planzone.uuid, "timeRecord");

  React.useEffect(() => {
    !!planzone.uuid &&
      !isTimeRecordsTemplatePending &&
      !isTimeRecordsTemplateSuccess &&
      !isTimeRecordsTemplateFailure &&
      retrieveSecondaryCriteriaTemplate({
        namespace: planzone.uuid,
        scope: "planzone",
        elementType: "timeRecord",
      });
  }, [planzone.uuid]);

  React.useEffect(() => {
    !isRequestPending &&
      !isTimeSheetFailure &&
      isDirty &&
      retrievePersonalTimeSheet({
        resource: account.resource,
        filter,
        page: 0,
        size: Math.max(timeSheet.projects.length, 30),
      });
  }, [isDirty, isRequestPending]);

  React.useEffect(() => {
    !teamRequestSuccess &&
      !teamRequestPending &&
      retrieveResourceSubordinates({
        resource: account.resource,
        page: 0,
        size: 5,
      });
    !retrievePlanzoneSettingsPending &&
      !retrievePlanzoneSettingsSuccess &&
      retrievePlanzoneSettings(planzone);
  }, []);

  React.useEffect(() => {
    !isProjectStatusesLoading &&
      !isProjectStatusesSuccessful &&
      !isProjectStatusesFailed &&
      retrieveProjectStatuses();
  }, []);

  return (
    <AutoFocusContext.Provider
      value={{
        lastAllocatedTaskUuid: lastAllocated?.taskUuid ?? null,
        lastAllocatedProjectUuid: lastAllocated?.projectUuid ?? null,
        lastAllocatedResourceUuid: lastAllocated?.resourceUuid ?? null,
        clearLastAllocated: () => setLastAllocated(null),
      }}
    >
      <TreeToggle key={showPersonalView ? "personal" : "team"}>
        <div className={styles.timeSheetPage}>
          <div className={styles.filters}>
            <div className={styles.filtersLeft}>
              <Navigation week={week} cursor={cursor} setCursor={setCursor} />
              <Filters filter={filter} setFilter={setFilter} />
              <DetailsToggle
                isToggled={!state.isTimeSheetMinimized}
                toggle={() =>
                  edit({
                    ...state,
                    isTimeSheetMinimized: !state.isTimeSheetMinimized,
                  })
                }
              />
              {settings.enableSelfAssignment && (
                <button
                  className={styles.button}
                  onClick={() =>
                    pushView({
                      key: UseCases.allocateResources,
                      data: {
                        planzone,
                        showPersonalView,
                        currentResource: account.resource,
                        onAllocated: (
                          taskUuid: string,
                          projectUuid: string,
                          resourceUuid: string,
                        ) =>
                          setLastAllocated({
                            taskUuid,
                            projectUuid,
                            resourceUuid,
                          }),
                      },
                    })
                  }
                >
                  {t("timeSheet.button")}
                </button>
              )}
            </div>
          </div>
          <div className={styles.header}>
            <div>
              <DisplayToggle
                isDisplayingTeam={!showPersonalView}
                isDisable={resources.length === 0}
                toggle={() => setShowPersonalView(!showPersonalView)}
              />
              {showPersonalView && <Controls />}
            </div>
            <div>
              <Month month={month} weekIndex={weekIndex} />
              <div className={styles.row}>
                <Week week={week} />
                {!state.isTimeSheetMinimized && (
                  <ResourceTimeOverview
                    showCompletionColumn={settings.showTaskCompletionPercentage}
                  />
                )}
              </div>
            </div>
          </div>
          <InfiniteScroll canFetchOnIdle>
            {() => (
              <>
                {showPersonalView ? (
                  <div className={styles.allocations}>
                    <Root
                      timeSheet={timeSheet}
                      filter={filter}
                      areDetailsDisplayed={!state.isTimeSheetMinimized}
                      showDeactivatedProjects={
                        !state.showDeactivatedProjectsOnTimeSheets
                      }
                      isReloading={isDirty && isRequestPending}
                      showCompletionColumn={
                        settings.showTaskCompletionPercentage
                      }
                    />
                    {isRequestPending && !isDirty && <Loader />}
                    <Flag
                      name="root"
                      isDisabled={isRequestPending || !canLoadMore}
                      retrieveItems={() =>
                        retrievePersonalTimeSheet({
                          resource: account.resource,
                          filter,
                          size: 30,
                          page: currentPage,
                        })
                      }
                    />
                  </div>
                ) : (
                  <div className={styles.allocations}>
                    <div className={styles.teamAllocations}>
                      {resources.map((resource) => (
                        <TeamAllocations
                          filters={filter}
                          resource={resource}
                          week={week}
                          key={resource.uuid}
                          showCompletionColumn={
                            settings.showTaskCompletionPercentage
                          }
                        />
                      ))}
                    </div>
                    <div className={styles.teamSubordinatesFlag}>
                      <Flag
                        name="teamSubordinates"
                        isDisabled={
                          teamRequestPending || !canLoadMoreSubordinates
                        }
                        retrieveItems={() =>
                          retrieveResourceSubordinates({
                            resource: account.resource,
                            page: subordinatesCurrentPage,
                            size: 5,
                          })
                        }
                      />
                    </div>
                  </div>
                )}
                {showPersonalView && (
                  <div className={styles.footer}>
                    <Total
                      dates={week}
                      timeSheet={timeSheet}
                      areDetailsDisplayed={!state.isTimeSheetMinimized}
                      resource={account.resource}
                    />
                  </div>
                )}
                <WeekTimeRecords
                  scope={
                    showPersonalView
                      ? { resourceUuid: account.resource.uuid }
                      : { managerUuid: account.resource.uuid }
                  }
                  week={week}
                  validationContext="resource"
                />
              </>
            )}
          </InfiniteScroll>
        </div>
      </TreeToggle>
    </AutoFocusContext.Provider>
  );
}

export { TimeSheet };
